import React, { useEffect, useState } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, Clock, Lock, Crown, Zap, ChevronRight, User, Calendar, MapPin, Star, Info } from 'lucide-react';
import RichText from '@/components/RichText';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import QuickActions from '@/components/QuickActions';
import { toast } from 'sonner';

const BACKEND_URL = '';

export default function CoursesPage({ user }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subStatus, setSubStatus] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [detailCourse, setDetailCourse] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/courses`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { credentials: 'include' }).then(r => r.json()),
    ])
      .then(([coursesData, sub]) => {
        setCourses(coursesData);
        setSubStatus(sub);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const isPremium = subStatus?.is_premium;
  const purchasedCourses = subStatus?.purchased_courses || [];

  const handleEnroll = async (courseId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}/enroll`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Enrollment failed');
      toast.success('Enrolled successfully!');
    } catch {
      toast.error('Enrollment failed. You may already be enrolled.');
    }
  };

  const handleBuyCourse = async (courseId) => {
    setPurchasing(courseId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Origin-URL': window.location.origin },
        body: JSON.stringify({ package_id: 'course', course_id: courseId }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Checkout failed');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setPurchasing(null);
    }
  };

  const canAccess = (courseId) => isPremium || purchasedCourses.includes(courseId);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="enablers-courses" type="top" className="mb-4 md:mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-4 md:mb-8 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading mb-1 md:mb-2 gradient-text flex items-center gap-2">Courses & Training <PageExplainer pageKey="courses" label="About Courses" /></h1>
              <p className="text-sm md:text-base text-slate-600">Learn from industry experts and advance your skills</p>
            </div>
          </div>

          {!isPremium && !loading && (
            <div data-testid="courses-paywall-banner" className="mb-8 p-5 rounded-xl bg-gradient-to-r from-brand-navy/5 to-brand-sage/10 border border-brand-blue/30">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-brand-navy mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-slate-800">Premium content</p>
                  <p className="text-sm text-slate-600">
                    Subscribe for full access to all courses, or purchase individual courses for £49 each.
                  </p>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map(course => {
                const hasAccess = canAccess(course.course_id);
                const isEnrolled = (course.enrolled_users || []).includes(user?.user_id);
                return (
                  <Card key={course.course_id} data-testid={`course-card-${course.course_id}`}
                    className={`p-6 border hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col h-full ${
                      hasAccess ? 'border-slate-200' : 'border-slate-200 opacity-95'
                    }`}>
                    {/* Top block — header + title + description.
                        `min-h` on the description reserves uniform vertical
                        space across cards so the modules + tag pills that
                        follow always line up at the same Y position
                        regardless of how short any one description is. */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <BookOpen className="h-8 w-8 text-brand-navy" />
                        <div className="flex items-center gap-2">
                          {course.coming_soon && (
                            <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full" data-testid={`coming-soon-${course.course_id}`}>
                              Coming Soon
                            </span>
                          )}
                          {course.delivery_mode && (
                            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              course.delivery_mode === 'in-person' ? 'bg-blue-100 text-blue-700'
                              : course.delivery_mode === 'hybrid' ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-600'
                            }`} data-testid={`delivery-mode-${course.course_id}`}>
                              {course.delivery_mode === 'in-person' ? 'In-Person'
                                : course.delivery_mode === 'hybrid' ? 'Hybrid'
                                : 'Online'}
                            </span>
                          )}
                          <span className="px-3 py-1 bg-brand-sage/20 text-brand-sage text-xs font-medium rounded-full">
                            {course.level}
                          </span>
                          {!hasAccess && <Lock className="h-4 w-4 text-slate-400" />}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold font-heading mb-2 min-h-[3.5rem]">{course.title}</h3>
                      <div className="text-sm text-slate-600 leading-relaxed min-h-[10rem]" data-testid={`course-card-intro-${course.course_id}`}>
                        <RichText html={course.intro || course.description} className="card card-6" />
                      </div>
                    </div>
                    <div className="space-y-2 mb-4">
                      {course.trainer_name && (
                        <div className="flex items-center text-sm text-slate-600" data-testid={`trainer-${course.course_id}`}>
                          <User className="h-4 w-4 mr-2 text-brand-navy" />
                          <span>Trainer: <strong>{course.trainer_name}</strong></span>
                        </div>
                      )}
                      {course.course_dates && (
                        <div className="flex items-center text-sm text-slate-600" data-testid={`dates-${course.course_id}`}>
                          <Calendar className="h-4 w-4 mr-2 text-brand-navy" />
                          <span>{course.course_dates}</span>
                        </div>
                      )}
                      {course.venue && (
                        <div className="flex items-center text-sm text-slate-600" data-testid={`venue-${course.course_id}`}>
                          <MapPin className="h-4 w-4 mr-2 text-brand-navy" />
                          <span>{course.venue}</span>
                        </div>
                      )}
                      {course.modules?.length > 0 && course.delivery_mode !== 'in-person' && (
                        <div className="flex items-center text-sm text-slate-600">
                          <Clock className="h-4 w-4 mr-2" />
                          <span>{course.modules.length} modules</span>
                        </div>
                      )}
                      {course.topic_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {course.topic_tags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Rating block + button cluster — `mt-auto` on this
                        wrapper anchors the button to the bottom so card
                        heights stay even when descriptions are short. */}
                    <div className="mt-auto">
                      {/* Rating Display */}
                      {(course.avg_rating > 0 || course.rating_count > 0) && (
                        <div className="flex items-center gap-1.5 mb-3" data-testid={`course-rating-${course.course_id}`}>
                          <div className="flex items-center">
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} className={`h-3.5 w-3.5 ${s <= Math.round(course.avg_rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                            ))}
                          </div>
                          <span className="text-sm font-semibold text-slate-700">{course.avg_rating || 0}</span>
                          <span className="text-xs text-slate-400">({course.rating_count || 0} {course.rating_count === 1 ? 'rating' : 'ratings'})</span>
                        </div>
                      )}

                      {course.coming_soon ? (
                        <Button
                          data-testid={`coming-soon-btn-${course.course_id}`}
                          disabled
                          className="w-full bg-slate-200 text-slate-500 cursor-not-allowed"
                        >
                          <Clock className="h-4 w-4 mr-2" />
                          Coming Soon
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <Button
                            data-testid={`start-course-${course.course_id}`}
                            onClick={() => {
                              // Fire-and-forget intent ping — if the user
                              // doesn't book within 24h they get one
                              // gentle nudge email. Server is idempotent
                              // per (user, course).
                              if (!isEnrolled) {
                                try {
                                  fetch(`${BACKEND_URL}/api/booking-intents`, {
                                    method: 'POST', credentials: 'include',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ target_type: 'course', target_id: course.course_id }),
                                    keepalive: true,
                                  }).catch(() => {});
                                } catch {}
                              }
                              navigate(`/courses/${course.course_id}`);
                            }}
                            className="w-full bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white"
                          >
                            <ChevronRight className="h-4 w-4 mr-2" />
                            {course.delivery_mode === 'in-person'
                              ? (isEnrolled ? 'View Booking' : (course.price > 0 ? `More Info & Book — £${course.price || 49}` : 'More Info & Book Free Place'))
                              : (isEnrolled ? 'Continue Learning' : course.price > 0 ? `View Course — £${course.price || 49}` : 'View Course — Free')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {!loading && courses.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500">No courses available at the moment</p>
            </div>
          )}

          <QuickActions user={user} exclude={['/courses']} />
            </div>
            <PageSideRail placement="enablers-courses" />
          </div>
        </div>
      </main>

      {/* More Detail modal — shows the full course description without
          forcing the user to navigate away from the listing. */}
      <Dialog open={!!detailCourse} onOpenChange={(v) => { if (!v) setDetailCourse(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="course-detail-dialog">
          {detailCourse && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold leading-tight pr-8" data-testid="course-detail-title">
                  {detailCourse.title}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                {detailCourse.level && (
                  <span className="px-2.5 py-0.5 bg-brand-sage/20 text-brand-sage font-medium rounded-full">{detailCourse.level}</span>
                )}
                {detailCourse.delivery_mode && (
                  <span className={`px-2.5 py-0.5 font-medium rounded-full ${
                    detailCourse.delivery_mode === 'in-person' ? 'bg-blue-100 text-blue-700'
                    : detailCourse.delivery_mode === 'hybrid' ? 'bg-purple-100 text-purple-700'
                    : 'bg-slate-100 text-slate-600'
                  }`}>
                    {detailCourse.delivery_mode === 'in-person' ? 'In-Person'
                      : detailCourse.delivery_mode === 'hybrid' ? 'Hybrid' : 'Online'}
                  </span>
                )}
                {(detailCourse.price || 0) > 0 && (
                  <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 font-semibold rounded-full">£{detailCourse.price}</span>
                )}
              </div>
              <div className="mt-4 text-sm text-slate-700 leading-relaxed" data-testid="course-detail-description">
                {(detailCourse.description || '').trim()
                  ? <RichText html={detailCourse.description} />
                  : <p className="text-slate-500 italic">No description has been added yet.</p>}
              </div>
              {detailCourse.what_you_will_learn && (
                <div className="mt-5 p-4 bg-brand-sage/5 border border-brand-sage/20 rounded-lg">
                  <h4 className="text-sm font-semibold text-brand-navy mb-2">What you&rsquo;ll learn</h4>
                  <RichText html={detailCourse.what_you_will_learn} className="text-sm text-slate-700 compact" />
                </div>
              )}
              <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDetailCourse(null)}
                  data-testid="course-detail-close"
                >
                  Close
                </Button>
                <Button
                  data-testid="course-detail-open-full"
                  onClick={() => { const id = detailCourse.course_id; setDetailCourse(null); navigate(`/courses/${id}`); }}
                  className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
                >
                  <ChevronRight className="h-4 w-4 mr-1" />
                  {detailCourse.delivery_mode === 'in-person'
                    ? ((detailCourse.price || 0) > 0 ? `Book Place — £${detailCourse.price || 49}` : 'Book Free Place')
                    : ((detailCourse.price || 0) > 0 ? `View Course — £${detailCourse.price || 49}` : 'View Course — Free')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
